import datapoints from '../datapoints';
import createAttachmentFromLocalFileService from '../services/createAttachmentFromLocalFile';
import CreateAttachmentResult from '../types/CreateAttachmentResult';
import { getErrorForAttachmentCreationFailure } from '../utils/getErrorForAttachmentCreationFailure';
import { shallowCopyAndReplaceValueNullWithUndefined } from '../utils/shallowCopyAndReplaceValueNullWithUndefined';
import { createLegacyActionWithDatapoint } from 'owa-analytics-actions';
import { AttachmentFile, CloudFile } from 'owa-attachment-file-types';
import { ClientItemId } from 'owa-client-ids';
import AttachmentInfoResponseMessage from 'owa-service/lib/contract/AttachmentInfoResponseMessage';
import AttachmentType from 'owa-service/lib/contract/AttachmentType';
import CreateAttachmentInlineType from 'owa-service/lib/contract/CreateAttachmentInlineType';
import CreateAttachmentResponse from 'owa-service/lib/contract/CreateAttachmentResponse';
import ItemIdAttachment from 'owa-service/lib/contract/ItemIdAttachment';
import itemIdAttachment from 'owa-service/lib/factory/itemIdAttachment';
import {
    AttachmentFullViewState,
    AttachmentState,
    AttachmentStateType,
    setActionCompletePercent,
} from 'owa-attachment-full-data';

// If you change the order of the arguments then also change the custom data function
// for the datapoint.
export default createLegacyActionWithDatapoint(
    'createAttachmentFromMailboxFile',
    datapoints.AttachmentCreationFromRecentAttachment
)(async function createAttachmentFromMailboxFile(
    parentItemId: ClientItemId,
    attachment: AttachmentType,
    cancellationId: string,
    backingObject: AttachmentFile,
    attachmentState: AttachmentState,
    isCloudFile: boolean
): Promise<CreateAttachmentResult> {
    let onProgressHandler = null;

    if (attachmentState.attachmentType === AttachmentStateType.Attachment) {
        onProgressHandler = function(e: ProgressEvent) {
            const loaded = e.loaded / e.total;
            setActionCompletePercent(<AttachmentFullViewState>attachmentState, loaded);
        };
    }

    // filter out null properties and do not send them back to server
    let attachmentForServiceCommand = shallowCopyAndReplaceValueNullWithUndefined(attachment);

    if (isCloudFile) {
        const cloudFile = backingObject as CloudFile;
        attachmentForServiceCommand = getItemIdAttachment(
            attachmentForServiceCommand,
            cloudFile.fileId
        );
    }

    const response: CreateAttachmentResponse = await createAttachmentFromLocalFileService(
        parentItemId,
        attachmentForServiceCommand,
        cancellationId,
        null,
        onProgressHandler
    );

    const responseMessage: AttachmentInfoResponseMessage = response.ResponseMessages.Items[0];
    if (
        responseMessage &&
        responseMessage.Attachments.length > 0 &&
        responseMessage.Attachments[0]
    ) {
        const result: CreateAttachmentResult = {
            attachment: responseMessage.Attachments[0],
            sharingInfo: response.SharingInformation,
        };
        return result;
    }

    throw getErrorForAttachmentCreationFailure(responseMessage);
});

function getItemIdAttachment(
    attachment: AttachmentType,
    attachmentIdToAttach: string
): ItemIdAttachment {
    return itemIdAttachment({
        AttachmentId: attachment.AttachmentId,
        AttachmentIdToAttach: attachmentIdToAttach,
        ContentType: attachment.ContentType,
        Name: attachment.Name,
        Size: attachment.Size,
        Thumbnail: attachment.Thumbnail,
        ThumbnailMimeType: attachment.ThumbnailMimeType,
        CreateAttachmentInlineType: CreateAttachmentInlineType.NotInline,
    });
}
